// ── BeeLove API client — Edge Functions / Firebase Functions ─────────────
// All calls that need server-side secrets (Stripe, Twilio, Resend)
// go through this module → your backend endpoint.
//
// Local dev: firebase emulators:start OR vercel dev
// Production: deploy/functions OR vercel deploy

// Supabase Edge Functions base URL
const BASE = process.env.REACT_APP_API_BASE
  || (process.env.REACT_APP_SUPABASE_URL
      ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
      : 'https://api.beelove.me');

// Get current Supabase session token
async function getToken() {
  try {
    const { supabase } = await import('./supabase');
    const { data: { session } } = await supabase?.auth.getSession() || { data: {} };
    return session?.access_token || null;
  } catch { return null; }
}

async function call(path, method = 'GET', body = null, token = null) {
  if (!token) token = await getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Stripe payments ───────────────────────────────────────────────────────
export async function createPaymentIntent({ amount, currency = 'cad', eventId, userId }, token) {
  return call('/stripe/payment-intent', 'POST', { amount, currency, eventId, userId }, token);
}

export async function confirmPayment({ paymentIntentId, paymentMethodId }, token) {
  return call('/stripe/confirm', 'POST', { paymentIntentId, paymentMethodId }, token);
}

export async function createSubscription({ priceId, userId }, token) {
  return call('/stripe/subscribe', 'POST', { priceId, userId }, token);
}

export async function cancelSubscription({ subscriptionId }, token) {
  return call('/stripe/cancel-subscription', 'POST', { subscriptionId }, token);
}

export async function refundPayment({ paymentIntentId, amount }, token) {
  return call('/stripe/refund', 'POST', { paymentIntentId, amount }, token);
}

// ── Email (Resend) ────────────────────────────────────────────────────────
export async function sendEmailConfirmation({ to, name, subject, eventTitle, eventDate, eventLocation, receiptId, amount }, token) {
  return call('/email/event-confirmation', 'POST', { to, name, subject, eventTitle, eventDate, eventLocation, receiptId, amount }, token);
}

export async function sendOTPEmail({ to, code }, token) {
  return call('/email/otp', 'POST', { to, code }, token);
}

// ── SMS (Twilio) ──────────────────────────────────────────────────────────
export async function sendOTPSms({ phone, code }, token) {
  return call('/sms/otp', 'POST', { phone, code }, token);
}

// ── Push notifications (FCM via server) ───────────────────────────────────
export async function sendPushToUser({ userId, title, body, data, type }, token) {
  return call('/notify', 'POST', { userId, title, body, data, type }, token);
}

export async function saveFCMToken({ userId, fcmToken }, token) {
  return call('/notifications/register-token', 'POST', { userId, fcmToken }, token);
}

// ── Photo moderation (Cloud Vision / AWS Rekognition) ────────────────────
export async function moderatePhoto({ photoUrl, userId }, token) {
  return call('/moderation/photo', 'POST', { photoUrl, userId }, token);
}

// ── Scouts — server-side group matching ─────────────────────────────────
export async function runScoutsMatching({ network, scoutType }, token) {
  return call('/scouts/compose', 'POST', { network, scoutType }, token);
}

// ── Bio generation (Anthropic key kept server-side) ──────────────────────
export async function generateBioFromAPI({ answers, profile }, token) {
  return call('/bio/generate', 'POST', { answers, profile }, token);
}

export { call as callAPI };
